import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Typography, Box } from '@mui/material';
import {
  SwitchStyled,
  SelectionBoxStyled,
  SelectionTitleStyled
} from '../style';
import { useAppSelector } from '../../../../store/hooks';
import calfTape from '../../../../assets/icons/calfTape.svg';
import { setGlim } from '../../../../store/slices/metaDataSlice';
import { metaDataSelector } from '../../../../store/globalSelectors';
import { PlaceholderGrid, CutOffIcon } from '../../../../styles/globalStyles';
import Line from '../../../../shared/components/line/index';
import {
  ToggleButtonStyled,
  ToggleButtonGroupStyled
} from '../../../../shared/components/toggleButtons/styles';
import { SingleNumberCard } from '../../../../shared/components/numberInput/numberCards/singleNumberCard';
import { glimConstants } from '../../../../shared/shared.constants';

export const CalfCircumference = () => {
  const dispatch = useDispatch();
  const {
    malnutritionScreening: { glim },
    patientInformation
  } = useAppSelector(metaDataSelector);

  const handleChangeHealthStatus = (event) => {
    dispatch(
      setGlim({
        ...glim,
        isPatientHealthy: event.target.value
      })
    );
  };

  const calculatedCircum = useMemo(() => {
    if (glim.calfCircumference === 0) return 0;
    let calculatedCircum = 0;
    if (glim.isPatientHealthy === 'one') {
      glimConstants.calfCalc.healthy.forEach((item) => {
        if (
          patientInformation.bmi >= item[0] &&
          patientInformation.bmi <= item[1]
        ) {
          calculatedCircum = Number(glim.calfCircumference) + item[2];
        }
      });
    } else {
      glimConstants.calfCalc.nothealthy.forEach((item) => {
        if (
          patientInformation.bmi >= item[0] &&
          patientInformation.bmi <= item[1]
        ) {
          calculatedCircum = Number(glim.calfCircumference) + item[2];
        }
      });
    }
    if (glim.isPatientHasOedema) {
      calculatedCircum = calculatedCircum - 2;
    }
    return calculatedCircum;
  }, [
    glim.calfCircumference,
    glim.isPatientHealthy,
    patientInformation.bmi,
    glim.isPatientHasOedema
  ]);

  const status: string =
    (patientInformation.gender.toLowerCase() === 'male' &&
      calculatedCircum < 34) ||
    (patientInformation.gender.toLowerCase() === 'female' &&
      calculatedCircum < 33)
      ? `Calf circumference suggests low muscle mass.`
      : `Calf circumference does not suggest low muscle mass.`;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <CutOffIcon>
          <img src={calfTape} alt="bmimeasure" />
        </CutOffIcon>
        <PlaceholderGrid
          container
          width={'100%'}
          sx={{ display: 'flex', gap: '1.875rem', flexDirection: 'column' }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '1.125rem',
                lineHeight: '1.5rem',
                textAlign: 'center',
                paddingTop: '1.25rem',
                textTransform: 'uppercase',
                color: '#002A3A',
                paddingBottom: '1.25rem',
                fontFamily: 'BGBold'
              }}
            >
              Calf circumference
            </Typography>
            <Box
              sx={{
                border: '0.0625rem solid #D9D9D6',
                borderRadius: '0.9375rem',
                width: '100%',
                textAlign: 'center'
              }}
              data-testid="calf-circumference-input"
            >
              <SingleNumberCard
                width={'100%'}
                title={'ENTER CALF CIRCUMFERENCE'}
                marginVal={'0.625rem'}
                range={{
                  min: 0,
                  max: 70
                }}
                subHead={'(cm)'}
                value={glim.calfCircumference}
                setValue={(val: number) => {
                  dispatch(
                    setGlim({
                      ...glim,
                      calfCircumference: val
                    })
                  );
                }}
              />
            </Box>
          </Box>
          <Line color="#D9D9D6" height="0.0625rem" />
          <Box>
            <Box sx={{ width: '100%', paddingBottom: '0.625rem' }}>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.25rem',
                  textAlign: 'left',
                  fontFamily: 'Calibri'
                }}
              >
                Choose which best fits your patient:
              </Typography>
            </Box>
            <ToggleButtonGroupStyled
              color="primary"
              value={glim.isPatientHealthy}
              exclusive
              onChange={handleChangeHealthStatus}
              aria-label="Platform"
              sx={{ padding: '0px', justifyContent: 'space-between' }}
            >
              <ToggleButtonStyled
                sx={{
                  width: '45%',
                  padding: '0.625rem 0.3125rem',
                  lineHeight: '1.25rem',
                  fontWeight: 400,
                  fontSize: '1rem',
                  fontFamily: 'Calibri'
                }}
                value="one"
              >
                Healthy
              </ToggleButtonStyled>
              <ToggleButtonStyled
                sx={{
                  width: '45%',
                  padding: '0.625rem 0.3125rem',
                  lineHeight: '1.25rem',
                  fontWeight: 400,
                  fontSize: '1rem',
                  fontFamily: 'Calibri'
                }}
                value="two"
              >
                Aging/Clinical population with probable muscle or weight loss
              </ToggleButtonStyled>
            </ToggleButtonGroupStyled>
          </Box>
          <Line color="#D9D9D6" height="0.0625rem" />
          <SelectionBoxStyled>
            <SelectionTitleStyled variant="h4">
              Does patient have lower limb oedema?
            </SelectionTitleStyled>
            <SwitchStyled
              checked={glim.isPatientHasOedema}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(
                  setGlim({
                    ...glim,
                    isPatientHasOedema: event.target.checked
                  })
                )
              }
              inputProps={{ 'aria-label': 'Switch-Toggle', role: 'checkbox' }}
              data-testid="isPatientHasOedemaSwitch"
            />
          </SelectionBoxStyled>
          <Line color="#D9D9D6" height="0.0625rem" />
          <Box
            sx={{
              border: '0.0625rem solid #D9D9D6',
              borderRadius: '0.9375rem',
              width: '100%',
              textAlign: 'center',
              backgroundColor: '#5BC2E740',
              display: 'flex',
              gap: '1.25rem',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '1.25rem 0'
            }}
          >
            <Typography
              sx={{
                fontWeight: 450,
                fontSize: '0.875rem',
                lineHeight: '0.9375rem',
                textAlign: 'center',
                textTransform: 'uppercase',
                fontFamily: 'BGBold'
              }}
            >
              ADJUSTED CALF CIRCUMFERENCE
            </Typography>
            <Box
              sx={{
                border: '0.0625rem solid #fff',
                borderRadius: '100px',
                textAlign: 'center',
                backgroundColor: '#fff',
                width: '35%'
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '35px',
                  lineHeight: '3.125rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  fontFamily: 'Calibri'
                }}
              >
                {calculatedCircum}cm
              </Typography>
            </Box>
            <Box
              sx={{
                border: '0.125rem solid #002A3A',
                borderRadius: '0.9375rem',
                textAlign: 'center',
                backgroundColor: '#fff',
                width: '80%',
                padding: '0.9375rem 0'
              }}
            >
              <Typography
                sx={{
                  fontWeight: 450,
                  fontSize: '1.125rem',
                  lineHeight: '1.375rem',
                  textAlign: 'center',
                  padding: '0.9375rem 0.625rem',
                  fontFamily: 'BGBold'
                }}
              >
                {status}
              </Typography>
            </Box>
          </Box>
        </PlaceholderGrid>
      </Box>
    </>
  );
};
